import { memo, useEffect, useRef, useState } from "react";

import { Button, IconButton, Tooltip, Typography } from "@mui/material";
import {
	CallbackProperty,
	Cartesian3,
	Cartographic,
	Clock,
	ClockRange,
	Color,
	createWorldTerrainAsync,
	HeadingPitchRange,
	HeadingPitchRoll,
	Ion,
	IonResource,
	JulianDate,
	ModelGraphics,
	PolygonGraphics,
	PolylineGraphics,
	SampledPositionProperty,
	sampleTerrainMostDetailed,
	SceneMode,
	Terrain,
	TrackingReferenceFrame,
	Transforms,
	Viewer,
} from "cesium";
import classNames from "classnames";
import {
	FaArrowDown,
	FaPause,
	FaPlane,
	FaPlay,
	FaRegMap,
	FaStop,
} from "react-icons/fa";
import { useSearchParams } from "react-router-dom";

import moment from "moment";
import {
	EHeightMeasurementUnit,
	ESpeedMeasurementUnit,
	THeartRate,
} from "../../../@types";
import { GoogleMapsService } from "../../../services/GoogleMapsService";
import { convertHeight, convertSpeed } from "../../../utils";
import { TLocation } from "../Student/FlightLogs/FlightLogs";
import {
	notifyNativeThreeDMapError,
	notifyNativeThreeDMapLoaded,
	runThreeDMapInitialization,
} from "./threeDMapBridge";

export type TThreeDMapCesiumProps = {
	gpsData: TLocation[];
	heartRates: THeartRate[] | null;
};

export const ThreeDMapCesium = memo(function ThreeDMapCesium(
	props: TThreeDMapCesiumProps,
) {
	const viewerRef = useRef<Viewer | null>(null);
	const countRef = useRef(0);
	const count2Ref = useRef(0);
	const timeSliderRef = useRef<HTMLInputElement>(null);
	const polygonRef = useRef<string[]>([]);
	const prevIndex = useRef(-2);
	const statusRef = useRef<"play" | "pause" | "stop">("pause");
	const [searchParams] = useSearchParams();
	const colorScheme = searchParams.get("color");
	const insetTop = searchParams.get("insetTop");
	const insetBottom = searchParams.get("insetBottom");

	type TState = {
		loading: boolean;
		landing: number | null;
		distance: number | null;
		speed: number;
		heading: number;
		height: number;
		wind: { windSpeed: number; windDirection: number } | null;
		status: "play" | "pause" | "stop";
		interval: number;
		elevation: number;
		cesiumHeight: number;
		currentTime: number;
		heartRate: number | null;
		loadError: boolean;
	};
	const [state, setState] = useState<TState>({
		loading: true,
		landing: 0,
		distance: 0,
		speed: 0,
		heading: 0,
		height: 0,
		wind: null,
		status: "stop",
		interval: 1000,
		elevation: 0,
		cesiumHeight: 0,
		currentTime: 0,
		heartRate: null,
		loadError: false,
	});
	const [selectedMode, setSelectedMode] = useState<"default" | "tail">(
		"default",
	);

	useEffect(() => {
		const handleMessage = () => {
			if (viewerRef.current) {
				const base64Image =
					viewerRef.current.scene.canvas.toDataURL("image/png");
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				window.ReactNativeWebView?.postMessage(`capture|${base64Image}`);
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				window.AndroidThreeDWebMessage?.sendCapturedImage(base64Image);
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				window.webkit?.messageHandlers?.iOSSendCapturedImage?.postMessage(
					base64Image,
				);
			}
		};
		window.addEventListener("capture3dimage", handleMessage);

		return () => {
			window.removeEventListener("capture3dimage", handleMessage);
		};
	}, [colorScheme]);

	useEffect(() => {
		let gpsData = props.gpsData;
		let elevation = 0;
		let cesiumHeight = 0;
		const scenePostRenderListener = () => {
			if (count2Ref.current % 10 === 0) {
				setState((prevState) => {
					if (prevState.loading) {
						const mainPath = viewerRef.current?.entities.getById("main-path");
						if (mainPath) {
							notifyNativeThreeDMapLoaded();
							return { ...prevState, loading: false, loadError: false };
						}
					}

					return prevState;
				});
			}
			count2Ref.current++;
		};

		const clockOnTickListener = (clock: Clock) => {
			if (countRef.current % 10 === 0) {
				const length = gpsData.length;
				const index = gpsData.findIndex(
					(point) =>
						point.time > JulianDate.toDate(clock.currentTime).getTime(),
				);
				if (timeSliderRef.current) {
					timeSliderRef.current.value = `${JulianDate.toDate(clock.currentTime).getTime() - gpsData[0].time}`;
				}

				if (index === -1) {
					setState((prevState) => ({
						...prevState,
						speed: gpsData[length - 1]?.speed ?? 0,
						heading: gpsData[length - 1]?.bearing ?? 0,
						height:
							gpsData[length - 1]?.mslAltitude ??
							gpsData[length - 1]?.altitude ??
							0,
						wind: gpsData[length - 1]?.calculatedData?.wind ?? null,
						currentTime: JulianDate.toDate(clock.currentTime).getTime(),
						status: "pause",
					}));
					statusRef.current = "pause";
				} else if (index < 2) {
					setState((prevState) => ({
						...prevState,
						speed: gpsData[0]?.speed ?? 0,
						heading: gpsData[0]?.bearing ?? 0,
						height: gpsData[0]?.mslAltitude ?? gpsData[0]?.altitude ?? 0,
						wind: gpsData[0]?.calculatedData?.wind ?? null,
						currentTime: JulianDate.toDate(clock.currentTime).getTime(),
					}));
				} else {
					setState((prevState) => ({
						...prevState,
						speed: gpsData[index - 1].speed ?? 0,
						heading: gpsData[index - 1]?.bearing ?? 0,
						height:
							gpsData[index - 1].mslAltitude ??
							gpsData[index - 1].altitude ??
							0,
						wind: gpsData[index - 1].calculatedData?.wind ?? null,
						currentTime: JulianDate.toDate(clock.currentTime).getTime(),
					}));
				}

				const heartRates = props.heartRates;
				if (heartRates && heartRates.length > 0) {
					const heartRateIndex = heartRates.findIndex(
						(heartRate) =>
							heartRate.time > JulianDate.toDate(clock.currentTime).getTime(),
					);
					if (heartRateIndex === -1) {
						setState((prevState) => ({
							...prevState,
							heartRate: heartRates[heartRates.length - 1].heartRate,
						}));
					} else if (heartRateIndex < 2) {
						setState((prevState) => ({
							...prevState,
							heartRate: heartRates[0].heartRate,
						}));
					} else {
						setState((prevState) => ({
							...prevState,
							heartRate: heartRates[heartRateIndex - 1].heartRate,
						}));
					}
				}

				if (index >= 0 && viewerRef.current && statusRef.current === "play") {
					const highlightPath =
						viewerRef.current.entities.getById("highlight-path");
					if (
						highlightPath?.polyline?.positions &&
						Math.abs(prevIndex.current - index) > 10
					) {
						(
							highlightPath.polyline as PolylineGraphics.ConstructorOptions
						).positions = Cartesian3.fromDegreesArrayHeights(
							gpsData
								.slice(
									Math.max(index - 60, 0),
									Math.min(index + 60, gpsData.length),
								)
								.flatMap((point) => [
									point.longitude,
									point.latitude,
									(point.mslAltitude ?? point.altitude) -
										elevation +
										cesiumHeight,
								]),
						);
						prevIndex.current = index;
					}
				}
			}
			countRef.current++;
		};

		const loadData = async () => {
			Ion.defaultAccessToken = process.env.REACT_APP_CESIUM_TOKEN ?? "";
			if (!viewerRef.current) {
				viewerRef.current = new Viewer("cesium-container", {
					terrain: Terrain.fromWorldTerrain({
						requestVertexNormals: true,
						requestWaterMask: true,
					}),
					fullscreenButton: false,
					homeButton: false,
					geocoder: false,
					sceneMode: SceneMode.SCENE3D,
					sceneModePicker: false,
					baseLayerPicker: false,
					navigationHelpButton: false,
					navigationInstructionsInitiallyVisible: false,
					contextOptions: {
						webgl: {
							preserveDrawingBuffer: true,
						},
					},
				});
			}
			viewerRef.current.scene.globe.undergroundColor = Color.TRANSPARENT;
			viewerRef.current.clock.onTick.addEventListener(clockOnTickListener);

			const planePositionProperty = new SampledPositionProperty();

			const startTime = JulianDate.fromDate(new Date(gpsData[0].time));
			const stopTime = JulianDate.fromDate(
				new Date(gpsData[gpsData.length - 1].time),
			);
			viewerRef.current.clock.startTime = startTime;
			viewerRef.current.clock.stopTime = stopTime;
			viewerRef.current.clock.currentTime = startTime;
			viewerRef.current.clock.clockRange = ClockRange.CLAMPED;
			viewerRef.current.timeline.zoomTo(startTime, stopTime);
			if (timeSliderRef.current) {
				timeSliderRef.current.min = "0";
				timeSliderRef.current.max = `${gpsData[gpsData.length - 1].time - gpsData[0].time}`;
				timeSliderRef.current.step = "10";
			}

			viewerRef.current.scene.postRender.addEventListener(
				scenePostRenderListener,
			);
			const terrainProvider = await createWorldTerrainAsync();

			const step = Math.ceil(gpsData.length / 512);
			const checkPoints = gpsData.filter((_, index) => index % step === 0);

			const { ElevationService } = await GoogleMapsService.loadElevation();
			const elevationResponse =
				await new ElevationService().getElevationForLocations({
					locations: checkPoints.map((point) => ({
						lat: point.latitude,
						lng: point.longitude,
					})),
				});
			elevation = elevationResponse.results[0].elevation;
			const heights = await sampleTerrainMostDetailed(
				terrainProvider,
				checkPoints.map((point) =>
					Cartographic.fromDegrees(point.longitude, point.latitude),
				),
			);
			cesiumHeight = heights[0].height;

			gpsData = gpsData.map((point, index) => {
				return {
					...point,
					cesiumHeight: heights[Math.floor(index / step)].height,
				};
			});

			for (let i = 0; i < gpsData.length; i++) {
				const position = Cartesian3.fromDegrees(
					gpsData[i].longitude,
					gpsData[i].latitude,
					(gpsData[i].mslAltitude ?? gpsData[i].altitude) -
						elevation +
						cesiumHeight -
						15,
				);
				planePositionProperty.addSample(
					JulianDate.fromDate(new Date(gpsData[i].time)),
					position,
				);
				if (i > 0) {
					polygonRef.current.push(`polygon-${i - 1}`);
					viewerRef.current.entities.add({
						id: `polygon-${i - 1}`,
						polygon: {
							hierarchy: Cartesian3.fromDegreesArrayHeights([
								gpsData[i - 1].longitude,
								gpsData[i - 1].latitude,
								(gpsData[i - 1].mslAltitude ?? gpsData[i - 1].altitude) -
									elevation +
									cesiumHeight,
								gpsData[i - 1].longitude,
								gpsData[i - 1].latitude,
								gpsData[i - 1].cesiumHeight ?? 0,
								gpsData[i].longitude,
								gpsData[i].latitude,
								gpsData[i].cesiumHeight ?? 0,
								gpsData[i].longitude,
								gpsData[i].latitude,
								(gpsData[i].mslAltitude ?? gpsData[i].altitude) -
									elevation +
									cesiumHeight,
							]),
							material: new Color(238 / 255, 238 / 255, 238 / 255, 0.2),
							perPositionHeight: true,
							closeBottom: false,
							closeTop: false,
						},
					});
				}
			}
			viewerRef.current.entities.add({
				id: "main-path",
				name: "Flight Path",
				polyline: {
					positions: Cartesian3.fromDegreesArrayHeights(
						gpsData.flatMap((point) => [
							point.longitude,
							point.latitude,
							(point.mslAltitude ?? point.altitude) - elevation + cesiumHeight,
						]),
					),
					width: 3,
					material: new Color(246 / 255, 170 / 255, 58 / 255, 1),
					clampToGround: false,
				},
			});

			const resource = await IonResource.fromAssetId(
				Number(process.env.REACT_APP_CESIUM_PLANE_MODEL ?? "0"),
			);
			viewerRef.current.entities.add({
				id: "plane-fake",
				position: planePositionProperty,
				orientation: new CallbackProperty((time) => {
					if (!time) {
						return;
					}
					const index = gpsData.findIndex(
						(point) => point.time > JulianDate.toDate(time).getTime(),
					);
					if (index === -1) {
						return;
					}
					const point = gpsData[index];
					if (
						point.heading === undefined ||
						point.pitch === undefined ||
						point.roll === undefined
					) {
						return;
					}

					const hpr = new HeadingPitchRoll(point.heading, 0, 0);

					return Transforms.headingPitchRollQuaternion(
						Cartesian3.fromDegrees(
							point.longitude,
							point.latitude,
							(point.mslAltitude ?? point.altitude) -
								elevation +
								cesiumHeight -
								15,
						),
						hpr,
					);
				}, false),
				model: {
					uri: resource,
					color: Color.TRANSPARENT,
				},
				trackingReferenceFrame: TrackingReferenceFrame.AUTODETECT,
				viewFrom: new Cartesian3(-400, 0, 100),
			});
			viewerRef.current.entities.add({
				id: "plane",
				position: planePositionProperty,
				orientation: new CallbackProperty((time) => {
					if (!time) {
						return;
					}
					const index = gpsData.findIndex(
						(point) => point.time > JulianDate.toDate(time).getTime(),
					);
					if (index === -1) {
						return;
					}
					const point = gpsData[index];
					if (
						point.heading === undefined ||
						point.pitch === undefined ||
						point.roll === undefined
					) {
						return;
					}

					const hpr = new HeadingPitchRoll(
						point.heading,
						point.pitch,
						point.roll,
					);

					return Transforms.headingPitchRollQuaternion(
						Cartesian3.fromDegrees(
							point.longitude,
							point.latitude,
							(point.mslAltitude ?? point.altitude) -
								elevation +
								cesiumHeight -
								15,
						),
						hpr,
					);
				}, false),
				model: {
					uri: resource,
					color: Color.TRANSPARENT,
				},
				trackingReferenceFrame: TrackingReferenceFrame.AUTODETECT,
				viewFrom: new Cartesian3(-400, 0, 100),
			});
			viewerRef.current.zoomTo(viewerRef.current.entities);
			setState((prevState) => ({
				...prevState,
				elevation,
				cesiumHeight,
			}));
		};

		if (gpsData.length > 1) {
			void runThreeDMapInitialization(loadData, (error) => {
				notifyNativeThreeDMapError(error);
				setState((prevState) => ({
					...prevState,
					loading: false,
					loadError: true,
				}));
			});
		} else {
			const error = new Error("Flight path requires at least two GPS points");
			notifyNativeThreeDMapError(error);
			setState((prevState) => ({
				...prevState,
				loading: false,
				loadError: true,
			}));
		}

		return () => {
			viewerRef.current?.scene.postRender.removeEventListener(
				scenePostRenderListener,
			);
			viewerRef.current?.clock.onTick.removeEventListener(clockOnTickListener);
		};
	}, [props.gpsData, props.heartRates]);

	const onClickPlay = () => {
		if (viewerRef.current && timeSliderRef.current) {
			viewerRef.current.clock.shouldAnimate = true;
			if (state.status === "stop") {
				viewerRef.current.clock.currentTime = JulianDate.fromDate(
					new Date(props.gpsData[0].time),
				);
				timeSliderRef.current.value = "0";
				const mainPath = viewerRef.current.entities.getById("main-path");
				if (mainPath?.polyline?.material) {
					(mainPath.polyline as PolylineGraphics.ConstructorOptions).material =
						new Color(238 / 255, 238 / 255, 238 / 255, 0.2);
				}
				for (let i = 0; i < polygonRef.current.length; i++) {
					const polygon = viewerRef.current.entities.getById(
						polygonRef.current[i],
					);
					if (polygon?.polygon?.material) {
						(polygon.polygon as PolygonGraphics.ConstructorOptions).material =
							new Color(238 / 255, 238 / 255, 238 / 255, 0.1);
					}
				}
				onClickFocusPlane();
				const plane = viewerRef.current.entities.getById("plane");
				if (plane?.model) {
					(plane.model as ModelGraphics.ConstructorOptions).color = Color.WHITE;
				}
				viewerRef.current.entities.add({
					id: "highlight-path",
					polyline: {
						positions: Cartesian3.fromDegreesArrayHeights(
							props.gpsData
								.slice(0, 60)
								.flatMap((point) => [
									point.longitude,
									point.latitude,
									(point.mslAltitude ?? point.altitude) -
										state.elevation +
										state.cesiumHeight,
								]),
						),
						width: 4,
						material: new Color(246 / 255, 170 / 255, 58 / 255, 1),
						clampToGround: false,
						zIndex: 10,
					},
				});
			}
		}
		setState((prevState) => ({ ...prevState, status: "play" }));
		statusRef.current = "play";
	};

	const onClickPause = () => {
		if (viewerRef.current) {
			viewerRef.current.clock.shouldAnimate = false;
		}
		setState((prevState) => ({ ...prevState, status: "pause" }));
		statusRef.current = "pause";
	};

	const onClickStop = () => {
		if (viewerRef.current && timeSliderRef.current) {
			viewerRef.current.zoomTo(viewerRef.current.entities);
			viewerRef.current.clock.shouldAnimate = false;
			viewerRef.current.clock.currentTime = JulianDate.fromDate(
				new Date(props.gpsData[0].time),
			);
			timeSliderRef.current.value = "0";
			const mainPath = viewerRef.current.entities.getById("main-path");
			if (mainPath?.polyline?.material) {
				(mainPath.polyline as PolylineGraphics.ConstructorOptions).material =
					new Color(246 / 255, 170 / 255, 58 / 255, 1);
				for (let i = 0; i < polygonRef.current.length; i++) {
					const polygon = viewerRef.current.entities.getById(
						polygonRef.current[i],
					);
					if (polygon?.polygon?.material) {
						(polygon.polygon as PolygonGraphics.ConstructorOptions).material =
							new Color(238 / 255, 238 / 255, 238 / 255, 0.2);
					}
				}
			}
			viewerRef.current.entities.removeById("highlight-path");
			const plane = viewerRef.current.entities.getById("plane");
			if (plane?.model) {
				(plane.model as ModelGraphics.ConstructorOptions).color =
					Color.TRANSPARENT;
			}
		}
		setState((prevState) => ({ ...prevState, status: "stop" }));
		statusRef.current = "stop";
	};

	const onClickInterval = (interval: number) => () => {
		if (viewerRef.current) {
			viewerRef.current.clock.multiplier = interval / 1000;
		}
		setState((prevState) => ({ ...prevState, interval }));
	};

	const onClickFocusPlane = () => {
		if (viewerRef.current) {
			const plane = viewerRef.current.entities.getById("plane");
			if (plane) {
				plane.trackingReferenceFrame = TrackingReferenceFrame.AUTODETECT;
			}
			const planeFake = viewerRef.current.entities.getById("plane-fake");
			if (planeFake) {
				planeFake.trackingReferenceFrame = TrackingReferenceFrame.AUTODETECT;
				viewerRef.current.trackedEntity = planeFake;
			}
		}
		setSelectedMode("default");
	};

	const onClickFocusPlaneTailView = () => {
		if (viewerRef.current) {
			const plane = viewerRef.current.entities.getById("plane");
			if (plane) {
				plane.trackingReferenceFrame = TrackingReferenceFrame.INERTIAL;
			}
			const planeFake = viewerRef.current.entities.getById("plane-fake");
			if (planeFake) {
				planeFake.trackingReferenceFrame = TrackingReferenceFrame.INERTIAL;
				viewerRef.current.trackedEntity = planeFake;
			}
		}
		setSelectedMode("tail");
	};

	const onClickFocusMap = () => {
		viewerRef.current?.zoomTo(viewerRef.current.entities);
	};

	const onClickTopDownView = () => {
		viewerRef.current?.zoomTo(
			viewerRef.current.entities,
			new HeadingPitchRange(0, -Math.PI / 2),
		);
	};

	let backgroundColor = "#FFFFFF";
	if (colorScheme === "dark") {
		backgroundColor = "#000000";
	}

	const currentTime = moment(state.currentTime);
	const timeString = `${currentTime.tz("UTC").format("DD MMM YYYY HH:mm:ss")}Z`;

	return (
		<div
			className="three-d-map"
			style={{
				width: "100vw",
				height: "100dvh",
				backgroundColor,
				position: "relative",
			}}>
			{state.loadError && (
				<div className="flight-map-status-message" role="alert">
					<Typography className="small-title-text" textAlign="center">
						The 3D replay could not be initialized. Please try again.
					</Typography>
				</div>
			)}
			<div id="cesium-container" className="cesium-containers">
				{!state.loading && props.gpsData.length > 1 && (
					<>
						<div
							className="map-view-controller"
							style={{ marginTop: `${insetTop ?? "0"}px` }}>
							{state.status !== "stop" && (
								<Tooltip title="Aircraft mode">
									<IconButton
										className={classNames(
											"icon-button map-view-controller-icon plane-icon",
											{ selected: selectedMode === "default" },
										)}
										onClick={onClickFocusPlane}>
										<FaPlane />
									</IconButton>
								</Tooltip>
							)}
							{state.status !== "stop" && (
								<Tooltip title="Tail view mode">
									<IconButton
										className={classNames(
											"icon-button map-view-controller-icon tail-view-plane-icon",
											{ selected: selectedMode === "tail" },
										)}
										onClick={onClickFocusPlaneTailView}>
										<FaPlane />
									</IconButton>
								</Tooltip>
							)}
							{state.status === "stop" && (
								<Tooltip title="Flight path mode">
									<IconButton
										className="icon-button map-view-controller-icon"
										onClick={onClickFocusMap}>
										<FaRegMap />
									</IconButton>
								</Tooltip>
							)}
							{state.status === "stop" && (
								<Tooltip title="Top down mode">
									<IconButton
										className="icon-button map-view-controller-icon"
										onClick={onClickTopDownView}>
										<FaArrowDown />
									</IconButton>
								</Tooltip>
							)}
						</div>
						{state.status !== "stop" && (
							<div
								className="top-left-info"
								style={{
									marginTop: `calc(${insetTop ? Number(insetTop) + 48 : 0}px`,
								}}>
								<Typography className="small-title-text replay-info">
									{`GS ${convertSpeed(state.speed, ESpeedMeasurementUnit.METERS_PER_SECOND, ESpeedMeasurementUnit.KNOTS).toFixed(0)} knots`}
								</Typography>
								{state.wind && (
									<Typography className="small-title-text replay-info">
										{`${state.wind.windDirection.toFixed(0)} ${state.wind.windSpeed.toFixed(0)} knots`}
									</Typography>
								)}
								{state.heartRate !== null && (
									<Typography className="small-title-text replay-info">
										{`${state.heartRate} BPM`}
									</Typography>
								)}
							</div>
						)}
						{state.status !== "stop" && (
							<div
								className="top-center-info"
								style={{
									marginTop: `calc(${insetTop ? Number(insetTop) + 48 : 0}px`,
								}}>
								<Typography className="small-title-text replay-info">
									{`TRK ${state.heading.toFixed(0)}`}
								</Typography>
							</div>
						)}
						{state.status !== "stop" && (
							<div
								className="top-right-info"
								style={{
									marginTop: `calc(${insetTop ? Number(insetTop) + 48 : 0}px`,
								}}>
								<Typography className="small-title-text replay-info">
									{`${convertHeight(state.height, EHeightMeasurementUnit.METER, EHeightMeasurementUnit.FOOT).toFixed(0)} ft MSL`}
								</Typography>
							</div>
						)}
						{state.status !== "stop" && (
							<div
								className="bottom-left-info"
								style={{ bottom: `calc(${insetBottom ?? "0"}px + 92px)` }}>
								<Typography className="small-title-text replay-info">
									{timeString}
								</Typography>
							</div>
						)}
					</>
				)}
				{props.gpsData.length > 1 && state.status === "stop" && (
					<div className="cesium-custom-controller">
						<Button
							className="normal-button primary-button no-transform"
							onClick={onClickPlay}>
							Start Playback
						</Button>
					</div>
				)}
				<div
					className="cesium-custom-controller"
					style={{
						display: state.loading || state.status === "stop" ? "none" : "flex",
						bottom: `calc(${insetBottom ?? 0}px + 24px)`,
					}}>
					<div style={{ display: "flex", alignItems: "center" }}>
						{state.status !== "play" && (
							<IconButton
								aria-label="Play flight playback"
								className="icon-button"
								onClick={onClickPlay}>
								<FaPlay className="normal-icon white-text" />
							</IconButton>
						)}
						{state.status === "play" && (
							<IconButton
								aria-label="Pause flight playback"
								className="icon-button"
								onClick={onClickPause}>
								<FaPause className="normal-icon white-text" />
							</IconButton>
						)}
						<IconButton
							aria-label="Stop flight playback"
							className="icon-button"
							onClick={onClickStop}>
							<FaStop className="normal-icon white-text" />
						</IconButton>
						<IconButton className="icon-button" onClick={onClickInterval(1000)}>
							<Typography
								className={classNames("normal-text", {
									"yellow-text": state.interval === 1000,
									"white-text": state.interval !== 1000,
								})}>
								x1
							</Typography>
						</IconButton>
						<IconButton className="icon-button" onClick={onClickInterval(2000)}>
							<Typography
								className={classNames("normal-text", {
									"yellow-text": state.interval === 2000,
									"white-text": state.interval !== 2000,
								})}>
								x2
							</Typography>
						</IconButton>
						<IconButton className="icon-button" onClick={onClickInterval(3000)}>
							<Typography
								className={classNames("normal-text", {
									"yellow-text": state.interval === 3000,
									"white-text": state.interval !== 3000,
								})}>
								x3
							</Typography>
						</IconButton>
						<IconButton className="icon-button" onClick={onClickInterval(5000)}>
							<Typography
								className={classNames("normal-text", {
									"yellow-text": state.interval === 5000,
									"white-text": state.interval !== 5000,
								})}>
								x5
							</Typography>
						</IconButton>
						<IconButton
							className="icon-button"
							onClick={onClickInterval(10000)}>
							<Typography
								className={classNames("normal-text", {
									"yellow-text": state.interval === 10000,
									"white-text": state.interval !== 10000,
								})}>
								x10
							</Typography>
						</IconButton>
					</div>
					<div style={{ flex: 1, width: "100%" }}>
						<input
							aria-label="Flight playback position"
							ref={timeSliderRef}
							type="range"
							id="time-slider"
							defaultValue="0"
							className="time-slider"
							onChange={(event) => {
								if (viewerRef.current) {
									viewerRef.current.clock.currentTime = JulianDate.fromDate(
										new Date(
											props.gpsData[0].time + Number(event.target.value),
										),
									);
								}
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
});
